import Component from '@glimmer/component'
import { tracked, cached } from '@glimmer/tracking'
import { on } from '@ember/modifier'
import { fn, get } from '@ember/helper'
import { t } from 'ember-intl'
import { eq } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon.gts'
import {
  EXEMPLAR_DATA,
  EXEMPLAR,
  type ExemplarData,
  type Annotation,
} from 'teacher-ui-client/utils/writing-legends/exemplar-data'
import {
  parseParagraphs,
  buildAnnotatedHtmlFromParagraphs,
} from 'teacher-ui-client/utils/writing-legends/build-annotated-html'

export type { Annotation }

const {
  ANNOTATION_STYLES: { BORDER_COLORS, BG_COLORS, HEX_COLORS, MAX_PER_EXEMPLAR },
} = EXEMPLAR

interface Signature {
  Element: HTMLDivElement
  Args: {
    annotations?: Annotation[]
    sourceText?: string
  }
}

export class ExemplarViewer extends Component<Signature> {
  @tracked focusedAnnotation: string | null = null

  @cached
  get annotations() {
    return this.args.annotations ?? []
  }

  isFocused = (annotationId: string) => annotationId === this.focusedAnnotation

  clickAnnotation = (annotationId: string) => {
    if (this.isFocused(annotationId)) {
      this.focusedAnnotation = null
    } else {
      this.focusedAnnotation = annotationId
    }
  }

  annotationBgColor = (index: number) => BG_COLORS[index % MAX_PER_EXEMPLAR] ?? 'bg-[#b0edf5]'
  annotationBorderColor = (index: number) => BORDER_COLORS[index % MAX_PER_EXEMPLAR] ?? 'border-[#b0edf5]'

  focusClasses = (annotationId: string) => {
    if (this.focusedAnnotation === null) return 'opacity-100'
    return this.isFocused(annotationId) ? 'opacity-100' : 'opacity-50'
  }

  tagFor = (tagId: string): ExemplarData => {
    return EXEMPLAR_DATA.find((tg) => tg.ID === tagId) ?? { ID: '', Category: '', Exemplar_Statement: '' }
  }

  // Tracks only @args.sourceText — stable after initial load, so DOMParser runs once.
  @cached
  get parsedParagraphs() {
    return parseParagraphs(this.args.sourceText ?? '')
  }

  // Tracks parsedParagraphs (cached) + focusedAnnotation (changes on eye-toggle).
  // DOMParser does not re-run on every focus change.
  get annotatedSourceHtml() {
    return buildAnnotatedHtmlFromParagraphs(
      this.parsedParagraphs,
      this.annotations,
      this.focusedAnnotation,
      HEX_COLORS,
      MAX_PER_EXEMPLAR,
    )
  }

  <template>
    <div class="flex flex-row space-x-2">
      {{! Left column: source text }}
      <div class="min-h-[200px] w-2/3 px-2 py-2 text-lg" data-test-source-text>
        {{this.annotatedSourceHtml}}
      </div>
      {{! Right column: annotation items }}
      <div class="w-1/3 pr-3">
        <ul>
          {{#each this.annotations as |annotation index|}}
            <li data-test-annotation-item class={{this.focusClasses annotation.id}}>
              <div class="flex items-center justify-between p-1 {{this.annotationBgColor index}}">
                <span class="p-1 text-white">{{get (this.tagFor annotation.tagId) "Category"}}</span>
                <button
                  type="button"
                  data-test-eye-toggle
                  class="h-6"
                  aria-label={{t
                    "pages.excitingWritingIdeas.previews.toggleAnnotationVisibility"
                    category=(get (this.tagFor annotation.tagId) "Category")
                  }}
                  aria-pressed={{if (this.isFocused annotation.id) "true" "false"}}
                  {{on "click" (fn this.clickAnnotation annotation.id)}}
                >
                  <PhosphorIcon
                    @icon={{if
                      (eq this.focusedAnnotation null)
                      "phosphor-eye"
                      (if (this.isFocused annotation.id) "phosphor-eye-fill" "phosphor-eye-slash")
                    }}
                    class="mr-1 h-full text-white"
                  />
                </button>
              </div>
              <div class="mb-2 border-4 p-2 {{this.annotationBorderColor index}}">
                <span class="font-bold">{{get (this.tagFor annotation.tagId) "Exemplar_Statement"}}</span>
                <div class="text-[#4e4e4e] italic">{{annotation.text}}</div>
              </div>
            </li>
          {{/each}}
        </ul>
      </div>
    </div>
  </template>
}

export default ExemplarViewer
